import { useState } from "react";
import { CompanyData, Product } from "@/contexts/GameContext";
import { useGame } from "@/contexts/GameContext";
import { useTheme } from "@/contexts/ThemeContext";

interface Props {
  company: CompanyData;
  isOwner: boolean;
}

const PRODUCT_CATEGORIES = [
  "SaaS", "Software", "Hardware", "食品", "衣料品", "電子機器",
  "医薬品", "建材", "自動車部品", "金融商品", "メディア", "物流サービス",
];

export default function ProductPanel({ company, isOwner }: Props) {
  const { addProduct, updateProduct } = useGame();
  const { isDark } = useTheme();
  const [showForm, setShowForm] = useState(false);
  const [msg, setMsg] = useState<{ text: string; type: "success" | "error" } | null>(null);
  const [form, setForm] = useState({
    name: "",
    category: "SaaS",
    description: "",
    productionCost: 500,
    sellingPrice: 2000,
    monthlyProduction: 50,
  });

  const cardBg = isDark ? "bg-[#0f1015] border-white/8" : "bg-white border-gray-200";
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-500";
  const inputClass = isDark
    ? "bg-white/5 border-white/10 text-white placeholder-gray-600 focus:border-amber-400/50"
    : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-amber-400";
  const divider = isDark ? "divide-white/5" : "divide-gray-100";
  const rowHover = isDark ? "hover:bg-white/3" : "hover:bg-gray-50";

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setMsg({ text: "製品名を入力してください", type: "error" });
      return;
    }
    const ok = addProduct(company.id, form);
    if (ok) {
      setMsg({ text: `「${form.name}」を製品ラインナップに追加しました！`, type: "success" });
      setForm({ name: "", category: "SaaS", description: "", productionCost: 500, sellingPrice: 2000, monthlyProduction: 50 });
      setShowForm(false);
    } else {
      setMsg({ text: "製品の追加に失敗しました", type: "error" });
    }
    setTimeout(() => setMsg(null), 3000);
  };

  const totalMonthlyRevenue = company.products
    .filter((p) => p.status === "active")
    .reduce((sum, p) => sum + p.sellingPrice * p.monthlyProduction, 0);

  const getStatusBadge = (status: Product["status"]) => {
    if (status === "active") return "bg-emerald-400/15 text-emerald-400";
    if (status === "development") return "bg-amber-400/15 text-amber-400";
    return isDark ? "bg-white/5 text-gray-500" : "bg-gray-100 text-gray-400";
  };

  const getStatusLabel = (status: Product["status"]) => {
    if (status === "active") return "販売中";
    if (status === "development") return "開発中";
    return "販売終了";
  };

  return (
    <div className={`rounded-2xl border overflow-hidden ${cardBg}`}>
      {/* Header */}
      <div className={`px-6 py-4 border-b ${isDark ? "border-white/5" : "border-gray-100"} flex items-center justify-between`}>
        <div>
          <h2 className={`text-base font-bold ${textPrimary}`}>製品・サービス管理</h2>
          <p className={`text-xs ${textSecondary} mt-0.5`}>
            月次製品収益予測: <span className="text-emerald-400 font-bold">{totalMonthlyRevenue.toLocaleString()} coins</span>
          </p>
        </div>
        {isOwner && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 text-sm px-4 py-2 rounded-xl bg-amber-400/15 text-amber-400 hover:bg-amber-400/25 border border-amber-400/30 transition-all cursor-pointer whitespace-nowrap font-semibold"
          >
            <i className={showForm ? "ri-close-line" : "ri-add-line"}></i>
            {showForm ? "キャンセル" : "新製品追加"}
          </button>
        )}
      </div>

      {/* Message */}
      {msg && (
        <div className={`mx-6 mt-4 rounded-xl px-4 py-2.5 text-sm flex items-center gap-2 ${msg.type === "success" ? "bg-emerald-400/10 text-emerald-400 border border-emerald-400/20" : "bg-red-400/10 text-red-400 border border-red-400/20"}`}>
          <i className={msg.type === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"}></i>
          {msg.text}
        </div>
      )}

      {/* Add Product Form */}
      {showForm && isOwner && (
        <form onSubmit={handleSubmit} className={`mx-6 my-4 rounded-xl border p-5 space-y-4 ${isDark ? "bg-white/3 border-white/8" : "bg-gray-50 border-gray-200"}`}>
          <h3 className={`text-sm font-bold ${textPrimary}`}>新製品・サービスを追加</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={`text-xs ${textSecondary} block mb-1.5`}>製品名 *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="例: AI Analytics Suite"
                className={`w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors ${inputClass}`}
              />
            </div>
            <div>
              <label className={`text-xs ${textSecondary} block mb-1.5`}>カテゴリ</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className={`w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors cursor-pointer ${inputClass}`}
              >
                {PRODUCT_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className={`text-xs ${textSecondary} block mb-1.5`}>製品説明</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="製品・サービスの説明を入力..."
                rows={2}
                maxLength={500}
                className={`w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors resize-none ${inputClass}`}
              />
            </div>
            <div>
              <label className={`text-xs ${textSecondary} block mb-1.5`}>製造コスト (coins/個)</label>
              <input
                type="number"
                value={form.productionCost}
                onChange={(e) => setForm({ ...form, productionCost: Number(e.target.value) })}
                min={1}
                className={`w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors ${inputClass}`}
              />
            </div>
            <div>
              <label className={`text-xs ${textSecondary} block mb-1.5`}>販売価格 (coins/個)</label>
              <input
                type="number"
                value={form.sellingPrice}
                onChange={(e) => setForm({ ...form, sellingPrice: Number(e.target.value) })}
                min={1}
                className={`w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors ${inputClass}`}
              />
            </div>
            <div>
              <label className={`text-xs ${textSecondary} block mb-1.5`}>月次生産数</label>
              <input
                type="number"
                value={form.monthlyProduction}
                onChange={(e) => setForm({ ...form, monthlyProduction: Number(e.target.value) })}
                min={1}
                className={`w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors ${inputClass}`}
              />
            </div>
            <div className={`rounded-lg p-3 ${isDark ? "bg-emerald-400/5 border border-emerald-400/15" : "bg-emerald-50 border border-emerald-200"}`}>
              <p className={`text-xs ${textSecondary} mb-1`}>月次収益予測</p>
              <p className="text-emerald-400 font-bold text-lg">
                {(form.sellingPrice * form.monthlyProduction).toLocaleString()} coins
              </p>
              <p className={`text-xs ${textSecondary}`}>
                利益: {((form.sellingPrice - form.productionCost) * form.monthlyProduction).toLocaleString()} coins
              </p>
            </div>
          </div>
          <button
            type="submit"
            className="w-full bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-black font-bold rounded-xl py-3 text-sm transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-circle-line mr-2"></i>
            製品を追加する
          </button>
        </form>
      )}

      {/* Product List */}
      {company.products.length === 0 ? (
        <div className="px-6 py-12 text-center">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-amber-400/10">
            <i className="ri-box-3-line text-2xl text-amber-400"></i>
          </div>
          <p className={`text-sm ${textSecondary}`}>製品がありません</p>
          {isOwner && (
            <button onClick={() => setShowForm(true)} className="mt-3 text-xs text-amber-400 hover:underline cursor-pointer">
              最初の製品を追加する →
            </button>
          )}
        </div>
      ) : (
        <div className={`divide-y ${divider}`}>
          {company.products.map((product) => {
            const margin = product.sellingPrice - product.productionCost;
            const marginRate = Math.round((margin / product.sellingPrice) * 100);
            const monthlyRevenue = product.sellingPrice * product.monthlyProduction;
            return (
              <div key={product.id} className={`px-6 py-4 transition-colors ${rowHover}`}>
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
                    <i className="ri-box-3-line text-amber-400 text-lg"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className={`text-sm font-semibold ${textPrimary}`}>{product.name}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap font-medium ${getStatusBadge(product.status)}`}>
                        {getStatusLabel(product.status)}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${isDark ? "bg-white/5 text-gray-400" : "bg-gray-100 text-gray-500"}`}>
                        {product.category}
                      </span>
                    </div>
                    {product.description && (
                      <p className={`text-xs ${textSecondary} mb-2`}>{product.description}</p>
                    )}
                    <div className="flex flex-wrap gap-4 text-xs">
                      <span className={textSecondary}>製造コスト: <span className={`font-semibold ${textPrimary}`}>{product.productionCost.toLocaleString()}</span></span>
                      <span className={textSecondary}>販売価格: <span className="font-semibold text-amber-400">{product.sellingPrice.toLocaleString()}</span></span>
                      <span className={textSecondary}>粗利率: <span className={`font-semibold ${marginRate >= 50 ? "text-emerald-400" : marginRate >= 20 ? "text-amber-400" : "text-red-400"}`}>{marginRate}%</span></span>
                      <span className={textSecondary}>月産: <span className={`font-semibold ${textPrimary}`}>{product.monthlyProduction}個</span></span>
                      <span className={textSecondary}>在庫: <span className={`font-semibold ${textPrimary}`}>{product.stockCount}個</span></span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-emerald-400 font-bold text-sm whitespace-nowrap">+{monthlyRevenue.toLocaleString()}</p>
                    <p className={`text-xs ${textSecondary}`}>coins/月</p>
                    {isOwner && (
                      <button
                        onClick={() => updateProduct(company.id, product.id, {
                          status: product.status === "active" ? "discontinued" : "active"
                        })}
                        className={`mt-2 text-xs px-2.5 py-1 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                          product.status === "active"
                            ? isDark ? "bg-white/5 text-gray-400 hover:bg-red-500/10 hover:text-red-400" : "bg-gray-100 text-gray-500 hover:bg-red-50 hover:text-red-500"
                            : "bg-emerald-400/10 text-emerald-400 hover:bg-emerald-400/20"
                        }`}
                      >
                        {product.status === "active" ? "停止" : "再開"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
